import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, getCurrentFinancialYear } from "../../../utils/commonUtil";
import { REIButton } from "../../../components/ui";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import EditTaxAndDepreciation from "./EditTaxAndDepreciation";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

interface ITaxAndDepreciation {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  depreciation: number;
  taxRefund: number;
  landTax: number;
}

interface ITaxAndDepreciationSummary {
  type: string;
  depreciation: number;
  taxRefund: number;
  landTax: number;
}

const TaxAndDepreciation: React.FC<{
  cashFlowData: any;
  isForecastOnly: boolean;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
}> = ({ cashFlowData, isForecastOnly, year, isEditable, setIsEditable }) => {
  const currentYear = "20" + getCurrentFinancialYear();
  const [transformedData, setTransformedData] = useState<ITaxAndDepreciation[]>([]);
  const [transformedFooter, setTransformedFooter] = useState<ITaxAndDepreciationSummary[]>([]);
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  useEffect(() => {
    if (cashFlowData && Array.isArray(cashFlowData.PropertyDetails) && cashFlowData.PropertyDetails.length > 0) {
      let groupCounter: number = -1;
      let lastGroupKey: string = "";
      const transformed = cashFlowData.PropertyDetails.map((properties: any) => {
        const groupKey = properties.PropertyDetail.StreetAddress;
        if (groupKey !== lastGroupKey) {
          groupCounter++;
          lastGroupKey = groupKey;
        }
        const actual = isShowingTotals
          ? properties.Details.CashflowDepreciationTotal?.Actuals?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowDepreciationPortion?.Actuals?.find((item: any) => item.Year === year) || {};
        const forecast = isShowingTotals
          ? properties.Details.CashflowDepreciationTotal?.Forecast?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowDepreciationPortion?.Forecast?.find((item: any) => item.Year === year) || {};
        const variance =
          Number(year) <= Number(currentYear)
            ? isShowingTotals
              ? properties.Details.CashflowDepreciationTotal?.Variance?.find((item: any) => item.Year === year) || {}
              : properties.Details.CashflowDepreciationPortion?.Variance?.find((item: any) => item.Year === year) || {}
            : {};

        return [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }) => ({
          street: properties.PropertyDetail?.StreetAddress || "",
          suburb: properties.PropertyDetail?.Suburb || "",
          state: properties.PropertyDetail?.State || "",
          postcode: properties.PropertyDetail?.Postcode || "",
          country: properties.PropertyDetail?.Country || "",
          type,
          depreciation: Number(data.Depreciation) || 0,
          taxRefund: Number(data.TaxRebate) || 0,
          landTax: Number(data.LandTax) || 0,
          propertyId: properties.Details?.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        }));
      }).flat();

      setTransformedData(transformed);

      const footerProperties = cashFlowData.TotalForDetails.find((item: any) => item.EntityId === "") || {};
      const actualFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Depreciation?.Actuals?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Depreciation?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecastFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Depreciation?.Forecast?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Depreciation?.Forecast?.find((item: any) => item.Year === year) ||
          {};
      const varianceFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.Depreciation?.Variance?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.Depreciation?.Variance?.find((item: any) => item.Year === year) ||
          {};

      const footer = [
        {
          type: "Actuals",
          depreciation: Number(actualFooter.Depreciation) || 0,
          taxRefund: Number(actualFooter.TaxRebate) || 0,
          landTax: Number(actualFooter.LandTax) || 0,
        },
        {
          type: "Forecast",
          depreciation: Number(forecastFooter.Depreciation) || 0,
          taxRefund: Number(forecastFooter.TaxRebate) || 0,
          landTax: Number(forecastFooter.LandTax) || 0,
        },
        {
          type: "Variance",
          depreciation: Number(varianceFooter.Depreciation) || 0,
          taxRefund: Number(varianceFooter.TaxRebate) || 0,
          landTax: Number(varianceFooter.LandTax) || 0,
        },
      ];
      setTransformedFooter(footer);
    }
  }, [cashFlowData, year, isShowingTotals]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const renderFooter = (category: any, data: ITaxAndDepreciationSummary) =>
    transformedFooter && (
      <Row>
        <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
        <Column field="type" footer={category} />
        <Column footer={currencyTemplate(data?.depreciation)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.taxRefund)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.landTax)} footerStyle={{ textAlign: "right" }} />
      </Row>
    );

  const footerGroup =
    transformedFooter &&
    (Number(year) > Number(currentYear) ? (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Actual"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Variance"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
      </ColumnGroup>
    ) : (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && renderFooter("Actuals", transformedFooter.find((x) => x.type == "Actuals")!)}
        {!isForecastOnly && renderFooter("Variance", transformedFooter.find((x) => x.type == "Variance")!)}
      </ColumnGroup>
    ));

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.street] || rowData.type === "Forecast"
      : !collapsedRows[rowData.street] || rowData.type === "Forecast";

    if (
      Number(year) > Number(currentYear) &&
      rowData.type !== "Forecast" &&
      rowData[field] !== "Actuals" &&
      rowData[field] !== "Variance"
    ) {
      return null;
    }

    return shouldRender ? templateFn(rowData[field]) : null;
  };

  return isEditable ? (
    <EditTaxAndDepreciation
      cashFlowData={cashFlowData}
      setIsEditable={setIsEditable}
      onCancel={function (): void | undefined {
        console.log("Function not implemented.");
      }}
      portfolioTrackerId={undefined}
      propertyName={undefined}
    />
  ) : (
    transformedData && transformedFooter && (
      <DataTable
        value={transformedData}
        rowGroupMode="rowspan"
        groupRowsBy="street"
        sortOrder={1}
        size="small"
        footerColumnGroup={footerGroup}
        rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "")}
      >
        <Column
          field="street"
          header="Address/property name"
          body={(rowData) => (
            <div className="flex items-center">
              {isForecastOnly && (
                <REIButton
                  id={`mcftd-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-down" : "pi pi-angle-right"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              {!isForecastOnly && (
                <REIButton
                  id={`mcftd-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-right" : "pi pi-angle-down"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              <span
                id={`mcftd-lnk-address-${rowData.propertyId}`}
                data-testid={`mcftd-lnk-address-${rowData.propertyId}`}
                className="cursor-pointer text-blue-700"
                onClick={() => {
                  navigate(`/portfolio-tracker`, {
                    state: {
                      tab: "MyPropertyTab",
                      MyPropertyId: rowData.propertyId,
                    },
                  });
                }}
              >{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
            </div>
          )}
        />
        <Column field="type" header="Type" body={(rowData) => renderCell(rowData, "type", (value) => value)} />
        <Column
          field="depreciation"
          header="Depreciation"
          body={(rowData) => renderCell(rowData, "depreciation", currencyTemplate)}
          align="right"
        />
        <Column
          field="taxRefund"
          header="Tax refund"
          body={(rowData) => renderCell(rowData, "taxRefund", currencyTemplate)}
          align="right"
        />
        <Column
          field="landTax"
          header="Land tax"
          body={(rowData) => renderCell(rowData, "landTax", currencyTemplate)}
          align="right"
        />
      </DataTable>
    )
  );
};

export default TaxAndDepreciation;
